import type { SelectProps, GetOptionPropsResult, OptionProps, DefaultGroup, DefaultItem, SelectAllItem } from 'uikit';
import {
    SelectItem,
    useForkRef,
    COMPONENT_NAME,
    IconSelect,
    SelectContainer,
    usePropsHandler,
    defaultPropSize,
    cnSelect,
    defaultLabelForEmptyItems,
    defaultPropForm,
    defaultPropView,
    withDefaultGetters,
    useDebounce,
} from 'uikit';

import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { SearchableSelectDropdown } from './components/SearchableSelectDropdown';
import { useSelect } from './hooks/useSelect';

type TSelectProps<ITEM, GROUP> = SelectProps<ITEM, GROUP> & {
    searchDebounceTime?: number;
    onSearch?: (value: string) => void;
    itemsSearchPredicate?: (item: SelectAllItem | ITEM, value: string) => void;
    isLazyLoadEnabled?: boolean;
    onNextPageRequest?: () => void;
    hasNextPage?: boolean;
};

export const SearchableSelect = forwardRef(
    <ITEM = DefaultItem, GROUP = DefaultGroup>(props: TSelectProps<ITEM, GROUP>, ref: React.Ref<HTMLDivElement>) => {
        const defaultDropdownRef = useRef<HTMLDivElement | null>(null);
        const controlRef = useRef<HTMLDivElement | null>(null);
        const searchInputRef = useRef<HTMLInputElement>(null);

        const {
            placeholder,
            onBlur,
            onFocus,
            items,
            onChange,
            value,
            required,
            disabled,
            ariaLabel,
            id,
            dropdownRef = defaultDropdownRef,
            form = defaultPropForm,
            view = defaultPropView,
            size = defaultPropSize,
            dropdownClassName,
            name,
            groups = [],
            getItemLabel,
            labelForEmptyItems = defaultLabelForEmptyItems,
            getItemKey,
            getItemGroupKey,
            getItemDisabled,
            getGroupKey,
            getGroupLabel,
            renderItem,
            isLoading,
            renderValue: renderValueProp,
            inputRef: inputRefProp,
            style,
            dropdownForm = 'default',
            ...restProps
        } = usePropsHandler(COMPONENT_NAME, withDefaultGetters(props), controlRef);

        const inputId = useMemo(() => (id ? `${id}-input` : id), [id]);

        const [searchValue, setSearchValue] = useState<string | null>(null);
        const [debounceSearchValue, setDebounceSearchValue] = useState<string>('');

        const onSearchDebouncedSearchValueChange = useDebounce((v) => {
            setDebounceSearchValue(v);
            props.onSearch?.(v);
        }, props.searchDebounceTime || 500);

        const onSearchValueChange = useCallback(
            ({ value: s }) => {
                setSearchValue(s);
                onSearchDebouncedSearchValueChange(s);
            },
            [onSearchDebouncedSearchValueChange, setSearchValue],
        );

        const filteredItems = useMemo(() => {
            const predicate =
                props.itemsSearchPredicate ||
                ((item: any, v: string) => item.label?.toLowerCase()?.includes(v.toLowerCase()));

            if (!debounceSearchValue) {
                return items;
            }

            const filtered = items?.filter((i: any) => predicate(i, debounceSearchValue)) || [];

            if (value && !filtered.find((v: any) => v.id === (value as any).id)) {
                return [...filtered, value].sort((a: any, b: any) => a.label?.localeCompare?.(b.label));
            }

            return filtered;
        }, [debounceSearchValue, items, props.itemsSearchPredicate, value]);

        const {
            getKeyProps,
            getOptionProps: getOptionPropsInitial,
            isOpen,
            visibleItems,
            handleInputFocus,
            handleInputBlur: handleInputBlurInitial,
            handleToggleDropdown,
            inputRef,
            handleInputClick,
            notFound,
            hasItems,
        } = useSelect<ITEM, GROUP, false>({
            items: filteredItems,
            groups,
            value,
            onChange,
            dropdownRef,
            controlRef,
            disabled,
            getItemLabel,
            getItemKey,
            getGroupKey,
            getItemGroupKey,
            getItemDisabled,
            multiple: false,
            onBlur,
            onFocus,
            isLazyLoadingEnabled: props.isLazyLoadEnabled,
            onSearchValueChange,
        });

        const handleInputBlur = useCallback(
            (e: React.FocusEvent<HTMLInputElement>): void => {
                if (e.nativeEvent.relatedTarget === searchInputRef.current) {
                    return;
                }

                handleInputBlurInitial(e);
            },
            [handleInputBlurInitial],
        );

        const getOptionProps = useCallback(
            (options?: OptionProps<any>): GetOptionPropsResult => {
                if (!options) {
                    const key = 'search-input';

                    return {
                        onClick: () => false,
                        onMouseEnter: () => false,
                        active: false,
                        hovered: false,
                        key,
                    };
                }

                return getOptionPropsInitial(options);
            },
            [getOptionPropsInitial],
        );

        const renderItemDefault = useCallback(
            (renderProps) => {
                const { item, active, hovered, onClick, onMouseEnter } = renderProps;

                return (
                    <SelectItem
                        label={getItemLabel(item)}
                        active={active}
                        hovered={hovered}
                        multiple={false}
                        size={size}
                        indent={dropdownForm === 'round' ? 'increased' : 'normal'}
                        onClick={onClick}
                        onMouseEnter={onMouseEnter}
                        disabled={getItemDisabled(item)}
                    />
                );
            },
            [dropdownForm, getItemDisabled, getItemLabel, size],
        );

        const renderValueDefault = useCallback(
            (renderProps) => {
                const label = getItemLabel(renderProps.item);

                return (
                    <span className={cnSelect('ControlValue')} title={label}>
                        {label}
                    </span>
                );
            },
            [getItemLabel],
        );

        return (
            <>
                <SelectContainer
                    disabled={disabled}
                    size={size}
                    view={view}
                    form={form}
                    required={required}
                    ref={ref}
                    style={style}
                    id={inputId}
                    {...restProps}
                >
                    <div
                        className={cnSelect('Control')}
                        ref={controlRef}
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        id={id}
                    >
                        <div className={cnSelect('ControlInner')}>
                            <div className={cnSelect('ControlValueContainer')}>
                                <input
                                    {...getKeyProps()}
                                    type="button"
                                    name={name}
                                    id={inputId}
                                    onFocus={handleInputFocus}
                                    onBlur={handleInputBlur}
                                    aria-label={ariaLabel}
                                    onClick={handleInputClick}
                                    ref={useForkRef([inputRef, inputRefProp])}
                                    className={cnSelect('FakeField')}
                                    readOnly
                                />
                                {value && (renderValueProp || renderValueDefault)({ item: value })}
                                {(typeof value === 'undefined' || value === null) && placeholder && (
                                    <span className={cnSelect('Placeholder')} title="placeholder">
                                        {placeholder}
                                    </span>
                                )}
                            </div>
                        </div>
                        <span className={cnSelect('Indicators')}>
                            <button
                                type="button"
                                className={cnSelect('IndicatorsDropdown')}
                                tabIndex={-1}
                                onClick={handleToggleDropdown}
                            >
                                <IconSelect size="xs" className={cnSelect('DropdownIndicatorIcon')} />
                            </button>
                        </span>
                    </div>
                </SelectContainer>
                <SearchableSelectDropdown
                    isOpen={isOpen}
                    size={size as any}
                    controlRef={controlRef}
                    getOptionProps={getOptionProps}
                    dropdownRef={dropdownRef}
                    form={dropdownForm}
                    className={dropdownClassName}
                    renderItem={(renderItem || renderItemDefault) as any}
                    getGroupLabel={getGroupLabel}
                    visibleItems={visibleItems}
                    notFound={notFound}
                    isLoading={isLoading}
                    labelForEmptyItems={labelForEmptyItems}
                    hasItems={hasItems}
                    style={typeof style?.zIndex === 'number' ? { zIndex: style.zIndex + 1 } : undefined}
                    searchInputRef={searchInputRef}
                    onSearch={onSearchValueChange}
                    searchValue={searchValue}
                    isLazyLoadEnabled={props.isLazyLoadEnabled}
                    onNextPageRequest={props.onNextPageRequest}
                    hasNextPage={props.hasNextPage}
                    items={filteredItems}
                />
            </>
        );
    },
);

SearchableSelect.displayName = 'TSearchableSelect';
